import { useEffect, useState, useRef, useCallback } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { toast } from "react-toastify";
import { useWhisperStt } from "./hooks/useWhisperStt";
import { SystemLanguage } from "../../common/system-language";

interface DictaphoneProps {
  onTranscriptChange: (text: string, resetTranscript: () => void, detectedLanguage?: string) => void;
  avatarLoading: boolean;
  avatarTalking: boolean;
  stopAudio: () => void;
  /**
   * BCP-47 tag for the Web Speech API (e.g. {@code "ar-EG"}). When undefined
   * (Auto Detect), the language is omitted from the recognition request so
   * the browser/engine performs its own detection.
   */
  languageCode?: string;
  avatarActions?: any;
  onListeningStart?: () => void;
  onListeningStop?: () => void;
  tapToTalk?: boolean;
  autoStart?: boolean;
  hideMic?: boolean;
  sessionResetCounter?: number;
  sttMode?: string;
  systemLanguage?: SystemLanguage;
  disabled?: boolean;
}

const DICTAPHONE_TRANSLATIONS: Record<SystemLanguage, Record<string, string>> = {
  en: {
    speaking: "Speaking...",
    processing: "Processing...",
    thinking: "Thinking...",
    tapToTalk: "Tap to talk",
    stopAvatar: "Stop avatar",
    interruptAndSpeak: "Interrupt and speak",
    cancel: "Cancel",
    send: "Send",
  },
  ar: {
    speaking: "يتحدث...",
    processing: "جاري المعالجة...",
    thinking: "يفكر...",
    tapToTalk: "اضغط للتحدث",
    stopAvatar: "إيقاف الأفاتار",
    interruptAndSpeak: "مقاطعة والتحدث",
    cancel: "إلغاء",
    send: "إرسال",
  },
};

const PROCESSING_TIMEOUT_MS = 20000;

/**
 * How long to wait for a final result after the Web Speech recogniser reports it stopped.
 *
 * Chrome routinely delivers the last `isFinal` result a beat AFTER `listening` drops. Anything
 * that falls back to the interim text has to wait out this window first, or the fallback and the
 * final both fire and the same utterance is sent twice.
 */
const FINAL_TRANSCRIPT_GRACE_MS = 700;

const WHISPER_WS_URL = process.env.REACT_APP_WHISPER_WS_URL;

/**
 * Web Speech error codes, and what they mean for the person holding the mic.
 *
 * react-speech-recognition only ever acts on `not-allowed`; every other code is swallowed by its
 * RecognitionManager, so without this the recogniser just stops and the UI drops back to idle with
 * no explanation — indistinguishable from "the mic didn't work".
 */
/**
 * Trace what we hand the Web Speech recogniser and what it ends up holding.
 *
 * `SpeechRecognition.lang` is a single value on a shared singleton recogniser, and
 * react-speech-recognition only writes it when a **truthy** language is passed that differs from
 * the current one — so an omitted or unchanged code silently leaves the previous utterance's
 * language in place. Logging both sides of the call is the only way to see that from outside.
 */
const logSpeechStart = (site: string, languageCode?: string) => {
  const recognition: any = SpeechRecognition.getRecognition();
  console.log(
    `[Dictaphone] startListening (${site}) — passing language "${languageCode ?? "(omitted)"}"; ` +
      `recogniser lang before "${recognition?.lang || "(unset)"}"`
  );
  // startListening is async and writes `lang` internally (after awaiting a stop, if one was
  // needed), so the applied value is not readable until the call has had a turn. This read is
  // provisional — the authoritative one is the "Web Speech listening" line, logged when the
  // recogniser actually reports itself running.
  setTimeout(() => {
    const after: any = SpeechRecognition.getRecognition();
    console.log(
      `[Dictaphone] startListening (${site}) — recogniser lang now "${after?.lang || "(unset)"}"`
    );
  }, 0);
};

const WEB_SPEECH_ERROR_MESSAGES: Record<string, string> = {
  "no-speech": "No speech detected — try again and start speaking right away.",
  "audio-capture": "No microphone found.",
  "not-allowed": "Microphone blocked — allow it in your browser's site settings.",
  "service-not-allowed":
    "Speech recognition is unavailable in this browser. Try Chrome.",
  network: "Speech recognition lost its connection. Check your network and try again.",
  "language-not-supported":
    "This browser cannot transcribe the selected speaker language.",
  "bad-grammar": "Speech recognition failed to start.",
};

const Dictaphone = ({
  onTranscriptChange,
  avatarLoading,
  avatarTalking,
  stopAudio,
  languageCode,
  avatarActions,
  onListeningStart,
  onListeningStop,
  tapToTalk = true,
  autoStart = false,
  hideMic = false,
  sessionResetCounter = 0,
  sttMode = "auto",
  systemLanguage = "en",
  disabled = false,
}: DictaphoneProps) => {
  const useWhisper = sttMode === "auto";
  const t = (key: string) =>
    DICTAPHONE_TRANSLATIONS[systemLanguage]?.[key] ||
    DICTAPHONE_TRANSLATIONS.en[key] ||
    key;

  // Which engine is live and what language reached the component. `sttMode` is the stored speaker
  // language: "auto" routes to Whisper, anything else to the Web Speech API.
  useEffect(() => {
    console.log(
      `[Dictaphone] engine=${useWhisper ? "whisper" : "web-speech"} ` +
        `sttMode="${sttMode}" languageCode="${languageCode ?? "(undefined)"}" ` +
        `tapToTalk=${tapToTalk} autoStart=${autoStart} disabled=${disabled}`
    );
  }, [useWhisper, sttMode, languageCode, tapToTalk, autoStart, disabled]);

  // ─── Shared state ───
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasSpokenInThisUtterance, setHasSpokenInThisUtterance] =
    useState(false);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Web Speech API (used when sttMode !== "auto") ───
  const {
    transcript,
    finalTranscript,
    listening,
    browserSupportsSpeechRecognition,
    resetTranscript,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  // ─── Whisper STT (used when sttMode === "auto") ───
  const [whisperTranscript, setWhisperTranscript] = useState("");
  const whisperTranscriptRef = useRef("");
  const [processingTranscriptPreview, setProcessingTranscriptPreview] = useState("");
  const whisperAutoRestartRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const resetWhisperTranscript = useCallback(() => {
    setWhisperTranscript("");
    whisperTranscriptRef.current = "";
  }, []);

  const handleWhisperInterim = useCallback((text: string) => {
    setWhisperTranscript(text);
    whisperTranscriptRef.current = text;
  }, []);

  const handleWhisperStopping = useCallback(() => {
    setIsProcessing(true);
    setProcessingTranscriptPreview(whisperTranscriptRef.current);
  }, []);

  const handleWhisperFinal = useCallback(
    (text: string, detectedLanguage?: string) => {
      const raw = text.trim();
      setWhisperTranscript("");
      whisperTranscriptRef.current = "";
      setProcessingTranscriptPreview("");

      if (!raw) {
        if (onListeningStop) onListeningStop();
        setIsRecording(false);
        setIsProcessing(false);
        return;
      }

      // Hands-free: stop avatar audio if it's playing before processing new input
      if (!tapToTalk && avatarTalking) {
        stopAudio();
      }

      if (onListeningStart) onListeningStart();
      onTranscriptChange(raw, resetWhisperTranscript, detectedLanguage);
      if (onListeningStop) onListeningStop();
      setIsRecording(false);
    },
    [
      tapToTalk,
      avatarTalking,
      stopAudio,
      onTranscriptChange,
      onListeningStart,
      onListeningStop,
      resetWhisperTranscript,
    ]
  );

  // Without this the UI parks in "Processing..." forever whenever STT dies (mic blocked, socket
  // refused, transcription error): the final transcript that clears it is never coming.
  const handleWhisperError = useCallback(
    (message: string) => {
      console.error("[Dictaphone] Whisper STT error:", message);
      setWhisperTranscript("");
      whisperTranscriptRef.current = "";
      setProcessingTranscriptPreview("");
      setIsRecording(false);
      setIsProcessing(false);
      // Resetting silently looks identical to "the mic just didn't work". Say what went wrong —
      // the capture layer reports a specific cause (denied, busy, disconnected, no device).
      // Fixed id: hands-free auto-restart can retry a failing mic repeatedly, and without this
      // each attempt stacks another toast.
      toast.error(message, { toastId: "dictaphone-stt-error" });
      if (onListeningStop) onListeningStop();
    },
    [onListeningStop]
  );

  const whisperStt = useWhisperStt({
    wsUrl: WHISPER_WS_URL,
    onInterimTranscript: handleWhisperInterim,
    onFinalTranscript: handleWhisperFinal,
    onStoppingListening: handleWhisperStopping,
    onError: handleWhisperError,
  });

  // Second line of defence: a transcription that never comes back (socket open but the backend
  // never answers) leaves no error to react to, so time the spinner out rather than hang.
  useEffect(() => {
    if (!isProcessing) return;
    const timeoutId = setTimeout(() => {
      console.warn("[Dictaphone] transcription timed out; resetting mic state");
      setIsProcessing(false);
      setProcessingTranscriptPreview("");
      setIsRecording(false);
      if (onListeningStop) onListeningStop();
    }, PROCESSING_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isProcessing, onListeningStop]);

  // ─── Clear processing state when avatar starts loading ───
  useEffect(() => {
    if (avatarLoading) {
      setIsProcessing(false);
    }
  }, [avatarLoading]);

  // ─── Mode switch: clean transition between tap-to-talk and hands-free ───
  useEffect(() => {
    if (!useWhisper) return;
    if (disabled) return;

    if (tapToTalk) {
      // Switched to tap-to-talk: stop any active listening
      if (whisperStt.isListening) {
        whisperStt.stopListening();
      }
      setIsRecording(false);
      resetWhisperTranscript();
    } else {
      // Switched to hands-free: force start listening immediately
      if (!whisperStt.isListening) {
        resetWhisperTranscript();
        setIsRecording(true);
        whisperStt.startListening();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapToTalk, disabled]);

  useEffect(() => {
    if (!disabled) return;

    if (useWhisper) {
      if (whisperStt.isListening) {
        whisperStt.cancelListening();
      }
      resetWhisperTranscript();
    } else {
      SpeechRecognition.stopListening();
      resetTranscript();
    }

    setIsRecording(false);
    setIsProcessing(false);
    setProcessingTranscriptPreview("");
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    if (whisperAutoRestartRef.current) {
      clearTimeout(whisperAutoRestartRef.current);
      whisperAutoRestartRef.current = null;
    }
    if (onListeningStop) onListeningStop();
    // Deps intentionally limited to [disabled, useWhisper]: this is a one-shot teardown that
    // should fire only when the component is disabled (or the STT engine changes). The other
    // values (whisperStt, resetTranscript, onListeningStop, …) are called imperatively — and
    // `whisperStt` is a fresh object every render, so listing it here re-runs this effect on
    // every render, and its stopListening()/resetTranscript() setState calls then loop forever
    // ("Maximum update depth exceeded"). Matches the reduced-dep pattern of the mode-switch
    // effects above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, useWhisper]);

  // ─── Whisper auto-start mode ───
  useEffect(() => {
    if (!useWhisper) return;
    if (disabled) return;
    if (!autoStart || tapToTalk) return;
    if (whisperStt.isListening || isRecording) return;

    whisperAutoRestartRef.current = setTimeout(() => {
      resetWhisperTranscript();
      whisperStt.startListening();
      setIsRecording(true);
    }, 200);

    return () => {
      if (whisperAutoRestartRef.current) {
        clearTimeout(whisperAutoRestartRef.current);
      }
    };
  }, [
    useWhisper,
    autoStart,
    tapToTalk,
    whisperStt.isListening,
    isRecording,
    avatarLoading,
    avatarTalking,
    resetWhisperTranscript,
    stopAudio,
    whisperStt,
    disabled,
  ]);

  // ─── Whisper: detect user started speaking (tap-to-talk) ───
  useEffect(() => {
    if (!useWhisper) return;
    if (tapToTalk === false) return;
    if (!onListeningStart) return;

    if (whisperStt.isListening) {
      if (!hasSpokenInThisUtterance && whisperTranscript.trim().length > 0) {
        setHasSpokenInThisUtterance(true);
        onListeningStart();
      }
    } else {
      if (hasSpokenInThisUtterance) {
        setHasSpokenInThisUtterance(false);
      }
    }
  }, [
    useWhisper,
    whisperStt.isListening,
    whisperTranscript,
    tapToTalk,
    hasSpokenInThisUtterance,
    onListeningStart,
  ]);

  // ═══════════════════════════════════════════════════════════
  // ─── Web Speech API: error plumbing ───
  // ═══════════════════════════════════════════════════════════

  /**
   * True once the recogniser has actually reported itself listening for the current utterance.
   * `listening` lags the click (startListening is async), so without this the "ended with no
   * result" recovery below would fire in the gap and cancel the recording it just started.
   */
  const webSpeechWasListeningRef = useRef(false);

  /**
   * One submission per utterance. The final-result path and the interim-salvage path below can
   * both become eligible for the same utterance; whichever gets there first claims it.
   */
  const submittedUtteranceRef = useRef(false);

  /** Called immediately before every SpeechRecognition.startListening(). */
  const beginWebSpeechUtterance = useCallback(() => {
    webSpeechWasListeningRef.current = false;
    submittedUtteranceRef.current = false;
  }, []);

  useEffect(() => {
    if (listening) webSpeechWasListeningRef.current = true;
  }, [listening]);

  const handleWebSpeechError = useCallback(
    (code: string) => {
      // `aborted` is emitted by every normal stopListening()/resetTranscript() — it is the
      // library's own teardown, not a failure.
      if (!code || code === "aborted") return;

      console.error(`[Dictaphone] Web Speech error: ${code}`);
      webSpeechWasListeningRef.current = false;
      setIsRecording(false);
      setIsProcessing(false);
      toast.error(
        WEB_SPEECH_ERROR_MESSAGES[code] || `Speech recognition failed (${code}).`,
        { toastId: "dictaphone-stt-error" }
      );
      if (onListeningStop) onListeningStop();
    },
    [onListeningStop]
  );

  // react-speech-recognition exposes no error callback, so hook the underlying recogniser and
  // chain the library's own handler (it still needs to see `not-allowed`).
  useEffect(() => {
    if (useWhisper) return;
    if (!browserSupportsSpeechRecognition) return;

    const recognition: any = SpeechRecognition.getRecognition();
    if (!recognition) return;

    const libraryOnError = recognition.onerror;
    recognition.onerror = (event: any) => {
      libraryOnError?.call(recognition, event);
      handleWebSpeechError(event?.error);
    };

    return () => {
      recognition.onerror = libraryOnError;
    };
  }, [useWhisper, browserSupportsSpeechRecognition, handleWebSpeechError]);

  // The tag settings stored is not necessarily the tag the engine honours, and a rejected tag is
  // silently swapped for the browser's locale rather than reported. Log both so a transcript that
  // comes back in the wrong language is diagnosable.
  useEffect(() => {
    if (useWhisper) return;
    if (!browserSupportsSpeechRecognition) return;
    if (!listening) return;

    const recognition: any = SpeechRecognition.getRecognition();
    console.log(
      `[Dictaphone] Web Speech listening — requested "${languageCode ?? "(none)"}", ` +
        `engine lang "${recognition?.lang || "(unset)"}"`
    );

    // The Web Speech API offers NO device selection — Chrome always records from the system
    // default input, and ignores anything chosen through navigator.mediaDevices. So when a headset
    // mic "doesn't work", the usual cause is that the OS default input is still the built-in mic.
    // List what the browser can see, and which one it considers default.
    navigator.mediaDevices
      ?.enumerateDevices?.()
      .then((devices) => {
        const inputs = devices.filter((d) => d.kind === "audioinput");
        console.log(
          "[Dictaphone] audio inputs (Web Speech always uses the system default):",
          inputs.map((d) => `${d.deviceId === "default" ? "* " : "  "}${d.label || "(label hidden)"}`)
        );
      })
      .catch(() => {});
  }, [useWhisper, browserSupportsSpeechRecognition, listening, languageCode]);

  // ═══════════════════════════════════════════════════════════
  // ─── Web Speech API: mode switch ───
  useEffect(() => {
    if (useWhisper) return;
    if (disabled) return;
    if (!browserSupportsSpeechRecognition) return;

    if (tapToTalk) {
      if (listening) {
        SpeechRecognition.stopListening();
      }
      setIsRecording(false);
      resetTranscript();
    } else {
      if (!avatarLoading && !avatarTalking && !listening) {
        resetTranscript();
        setIsRecording(true);
        // Omit `language` for Auto Detect (no fixed BCP-47 tag) — the
        // browser falls back to its own detection / configured default.
        beginWebSpeechUtterance();
        logSpeechStart("mode-switch/hands-free", languageCode);
        SpeechRecognition.startListening({
          ...(languageCode ? { language: languageCode } : {}),
          continuous: false,
          interimResults: true,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapToTalk, disabled]);

  // ═══════════════════════════════════════════════════════════
  // ─── Web Speech API effects (only when sttMode !== "auto") ───
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    if (useWhisper) return;
    if (disabled) return;
    if (!browserSupportsSpeechRecognition) return;
    if (tapToTalk === false) return;
    if (!onListeningStart) return;

    if (listening) {
      if (!hasSpokenInThisUtterance && transcript.trim().length > 0) {
        setHasSpokenInThisUtterance(true);
        onListeningStart();
      }
    } else {
      if (hasSpokenInThisUtterance) {
        setHasSpokenInThisUtterance(false);
      }
    }
  }, [
    useWhisper,
    listening,
    transcript,
    autoStart,
    tapToTalk,
    disabled,
    browserSupportsSpeechRecognition,
    hasSpokenInThisUtterance,
    onListeningStart,
  ]);

  useEffect(() => {
    if (useWhisper) return;
    if (!browserSupportsSpeechRecognition) return;

    const isAutoMode = autoStart && !tapToTalk;

    if (tapToTalk) {
      if (!listening && finalTranscript) {
        webSpeechWasListeningRef.current = false;
        const raw = (finalTranscript || transcript || "").trim();
        if (!raw) {
          if (onListeningStop) onListeningStop();
          resetTranscript();
          setIsRecording(false);
          return;
        }

        // The salvage path below already sent this utterance and Chrome delivered the final
        // afterwards. Drop it rather than send the same thing twice.
        if (submittedUtteranceRef.current) {
          resetTranscript();
          setIsRecording(false);
          return;
        }

        submittedUtteranceRef.current = true;
        setIsProcessing(true);
        onTranscriptChange(raw, resetTranscript);

        resetTranscript();
        setIsRecording(false);
        return;
      }

      // The recogniser ran and stopped without a final result — Chrome does this on a network
      // blip, a forced stop mid-word, or after `not-allowed` disables it. Nothing will arrive to
      // clear the capsule, so the UI would sit in "recording" with Cancel as the only way out.
      //
      // But a final result that is merely LATE looks identical at this instant, so wait out the
      // grace window before falling back to the interim text. If the final does turn up, this
      // effect re-runs with a non-empty finalTranscript, the branch above handles it, and the
      // cleanup below cancels this timer.
      if (
        !listening &&
        isRecording &&
        webSpeechWasListeningRef.current &&
        !submittedUtteranceRef.current
      ) {
        const salvageTimeoutId = setTimeout(() => {
          webSpeechWasListeningRef.current = false;
          const salvaged = (transcript || "").trim();
          resetTranscript();
          setIsRecording(false);

          if (salvaged) {
            submittedUtteranceRef.current = true;
            setIsProcessing(true);
            onTranscriptChange(salvaged, resetTranscript);
          } else if (onListeningStop) {
            onListeningStop();
          }
        }, FINAL_TRANSCRIPT_GRACE_MS);

        return () => clearTimeout(salvageTimeoutId);
      }

      return;
    }

    if (!isAutoMode) return;

    if (!listening && isAutoMode && isRecording && !submittedUtteranceRef.current) {
      // Claim the utterance before the await — this effect can re-run during that window.
      submittedUtteranceRef.current = true;
      const handleStoppedListening = async () => {
        await new Promise((resolve) => setTimeout(resolve, 200));

        const raw = (finalTranscript || transcript || "").trim();

        if (!raw) {
          submittedUtteranceRef.current = false;
          resetTranscript();
          setIsRecording(false);
          return;
        }

        setIsProcessing(true);
        if (onListeningStart) onListeningStart();
        onTranscriptChange(raw, resetTranscript);
        if (onListeningStop) onListeningStop();
        resetTranscript();
        setIsRecording(false);
      };

      handleStoppedListening();
    }
  }, [
    useWhisper,
    listening,
    transcript,
    finalTranscript,
    resetTranscript,
    onTranscriptChange,
    browserSupportsSpeechRecognition,
    onListeningStop,
    onListeningStart,
    autoStart,
    tapToTalk,
    isRecording,
    isMicrophoneAvailable,
    disabled,
  ]);

  useEffect(() => {
    if (useWhisper) return;
    if (disabled) return;
    if (!browserSupportsSpeechRecognition) return;

    if (!tapToTalk) {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      return;
    }

    if (!listening) {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      return;
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }

    silenceTimeoutRef.current = setTimeout(() => {
      // Only ask the recogniser to stop. Clearing `isRecording` here too was silently dropping
      // utterances: it took the UI back to idle before the result arrived, and the transcript
      // effect below — which is what actually submits — is gated on `isRecording`. Let that
      // effect own the transition; it now resolves whether or not a final result turns up.
      SpeechRecognition.stopListening();
    }, 2500);

    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    };
  }, [
    useWhisper,
    listening,
    transcript,
    browserSupportsSpeechRecognition,
    tapToTalk,
    disabled,
  ]);

  useEffect(() => {
    if (useWhisper) return;
    if (disabled) return;
    if (!browserSupportsSpeechRecognition) return;
    if (!autoStart || tapToTalk) return;
    if (avatarLoading || avatarTalking) return;

    if (!listening && !isRecording) {
      resetTranscript();
      stopAudio();

      const timeoutId = setTimeout(() => {
        beginWebSpeechUtterance();
        logSpeechStart("auto-restart/hands-free", languageCode);
        SpeechRecognition.startListening({
          ...(languageCode ? { language: languageCode } : {}),
          continuous: false,
          interimResults: true,
        });
        setIsRecording(true);
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [
    useWhisper,
    autoStart,
    tapToTalk,
    listening,
    isRecording,
    avatarLoading,
    avatarTalking,
    sttMode,
    browserSupportsSpeechRecognition,
    resetTranscript,
    stopAudio,
    languageCode,
    disabled,
    beginWebSpeechUtterance,
  ]);

  /**
   * Ask for the microphone explicitly instead of letting the engine do it implicitly, so a refusal
   * surfaces as a named error the tester can act on.
   *
   * Once permission is already granted we skip the probe entirely: opening a stream here and
   * stopping it a moment before the recorder opens its own is a real source of flakiness —
   * releasing and re-acquiring the device back-to-back races the OS audio stack and shows up as
   * NotReadableError ("mic is busy") or a start that silently captures nothing, which is exactly
   * the "mic fails to listen" symptom. The probe is only needed to raise the permission prompt
   * inside the user gesture, so run it only when permission is still unresolved.
   */
  const ensureMicrophonePermission = useCallback(async (): Promise<boolean> => {
    const mediaDevices = (navigator as any)?.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      console.warn(
        `[Dictaphone] no getUserMedia (isSecureContext=${window.isSecureContext}) — the origin must be https or localhost`
      );
      toast.error("Microphone unavailable: this page must be served over https.");
      return false;
    }

    // Not in every browser (Firefox has no "microphone" descriptor, Safari added it late), so
    // treat any failure here as "unknown" and fall through to the probe.
    try {
      const status = await (navigator as any).permissions?.query({
        name: "microphone" as PermissionName,
      });
      if (status?.state === "granted") {
        return true;
      }
      if (status?.state === "denied") {
        console.warn("[Dictaphone] microphone permission is denied for this origin");
        toast.error("Microphone blocked — allow it in your browser's site settings.");
        return false;
      }
    } catch {
      /* permissions API unavailable — fall through to the probe */
    }

    try {
      const stream: MediaStream = await mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error: any) {
      console.warn(
        `[Dictaphone] microphone permission failed: ${error?.name || "Error"} — ${error?.message || error}`
      );
      toast.error(`Microphone blocked (${error?.name || "error"}).`);
      return false;
    }
  }, []);

  /**
   * react-speech-recognition latches `isMicrophoneAvailable` to false on the first `not-allowed`
   * error and calls disableRecognition(), which strips the recognition handlers for good — nothing
   * in its API turns that back on. So once it trips, the Web Speech engine is dead until reload,
   * even after permission is granted. Say so instead of no-oping.
   */
  const webSpeechBlockedByLatch = () => {
    console.warn(
      "[Dictaphone] Web Speech recognition was disabled by an earlier 'not-allowed' error — reload the page to re-enable it"
    );
    toast.info("Microphone permission changed — reload the page to use the mic.");
  };

  // ─── Interrupt avatar and start recording ───
  const interruptAndRecord = async () => {
    if (disabled) return;
    if (!tapToTalk) return;
    if (avatarLoading) return;

    // Stop avatar if it's currently talking
    if (avatarTalking) {
      stopAudio();
    }

    if (useWhisper) {
      if (!whisperStt.isListening && !isRecording) {
        if (!(await ensureMicrophonePermission())) return;
        whisperStt.startListening();
        setIsRecording(true);
        if (onListeningStart) onListeningStart();
      }
      return;
    }

    if (!browserSupportsSpeechRecognition) return;
    if (!(await ensureMicrophonePermission())) return;
    if (!isMicrophoneAvailable) {
      webSpeechBlockedByLatch();
      return;
    }

    if (!listening && !isRecording) {
      beginWebSpeechUtterance();
      logSpeechStart("interrupt-and-record", languageCode);
      SpeechRecognition.startListening({
        ...(languageCode ? { language: languageCode } : {}),
        continuous: true,
        interimResults: true,
      });
      setIsRecording(true);
      if (onListeningStart) onListeningStart();
    }
  };

  // ─── Button click handler ───
  const updateRecordingStatus = async () => {
    if (disabled) return;
    if (!tapToTalk) return;
    if (avatarLoading) return;

    // If avatar is talking, interrupt and start listening
    if (avatarTalking) {
      await interruptAndRecord();
      return;
    }

    if (useWhisper) {
      if (!whisperStt.isListening && !isRecording) {
        if (!(await ensureMicrophonePermission())) return;
        whisperStt.startListening();
        setIsRecording(true);
        if (onListeningStart) onListeningStart();
      } else {
        whisperStt.stopListening();
        setIsRecording(false);
        stopAudio();
        if (onListeningStop) onListeningStop();
      }
      return;
    }

    // Web Speech API mode. Both guards below make the button a silent no-op, which is
    // indistinguishable from "the click didn't register" — say why in the console.
    if (!browserSupportsSpeechRecognition) {
      console.warn(
        "[Dictaphone] mic ignored: this browser has no Web Speech API (needs a secure origin — https or localhost)"
      );
      return;
    }
    if (!(await ensureMicrophonePermission())) return;
    if (!isMicrophoneAvailable) {
      webSpeechBlockedByLatch();
      return;
    }

    if (!listening && !isRecording) {
      beginWebSpeechUtterance();
      logSpeechStart("mic-button", languageCode);
      SpeechRecognition.startListening({
        ...(languageCode ? { language: languageCode } : {}),
        continuous: true,
        interimResults: true,
      });
      setIsRecording(true);
      if (onListeningStart) onListeningStart();
    } else {
      // Same as the silence timer: leave `isRecording` to the transcript effect, so the pending
      // result is still submitted instead of being dropped on the way back to idle.
      SpeechRecognition.stopListening();
      stopAudio();
    }
  };

  // ─── Cancel handler ───
  const handleCancel = () => {
    if (disabled) return;
    if (useWhisper) {
      whisperStt.cancelListening();
    } else {
      SpeechRecognition.stopListening();
      resetTranscript();
      // Cancel discards the utterance outright, so disarm the "ended with no result" recovery.
      webSpeechWasListeningRef.current = false;
    }
    setIsRecording(false);
    setIsProcessing(false);
    if (onListeningStop) onListeningStop();
  };

  // ─── Recording timer ───
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isActivelyListening = useWhisper ? whisperStt.isListening : listening;

  useEffect(() => {
    if (isActivelyListening) {
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingSeconds(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActivelyListening]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ─── Render ───

  if (hideMic) {
    if (avatarTalking) {
      return (
        <div className="audio-capture">
          <button
            onClick={stopAudio}
            id="speech"
            className="btn type2"
            disabled={disabled}
          >
            <i className="fa fa-stop" aria-hidden="true"></i>
          </button>
        </div>
      );
    }
    return null;
  }

  // ── Avatar talking: show talking capsule ──
  if (avatarTalking) {
    return (
      <div className="audio-capture capsule-active">
        <div className="voice-capsule talking-capsule">
          <button onClick={stopAudio} className="capsule-stop" title={t("stopAvatar")} disabled={disabled}>
            <i className="fa fa-stop" aria-hidden="true"></i>
          </button>

          <div className="capsule-center">
            <div className="talking-eq">
              <span></span><span></span><span></span>
              <span></span><span></span><span></span>
              <span></span>
            </div>
            <span className="talking-label">{t("speaking")}</span>
          </div>

          <button onClick={interruptAndRecord} className="capsule-mic" title={t("interruptAndSpeak")} disabled={disabled}>
            <i className="fa fa-microphone" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    );
  }

  // ── Recording: show voice capsule bar ──
  if (isActivelyListening) {
    return (
      <div className="audio-capture capsule-active">
        <div className="voice-capsule">
          <button onClick={handleCancel} className="capsule-cancel" title={t("cancel")} disabled={disabled}>
            <i className="fa fa-times" aria-hidden="true"></i>
          </button>

          <div className="capsule-center">
            <div className="rec-dot"></div>
            <div className="capsule-waveform">
              <span></span><span></span><span></span><span></span>
              <span></span><span></span><span></span><span></span>
              <span></span><span></span><span></span><span></span>
            </div>
            <div className="capsule-timer">{formatTime(recordingSeconds)}</div>
          </div>

          <button onClick={updateRecordingStatus} className="capsule-send" title={t("send")} disabled={disabled}>
            <i className="fa fa-paper-plane" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    );
  }

  // ── Processing: show compact pill with transcript preview ──
  if (isProcessing) {
    return (
      <div className="audio-capture processing-active">
        <div className="processing-pill">
          <div className="processing-spinner"></div>
          <span className="processing-label">
            {processingTranscriptPreview
              ? `"${processingTranscriptPreview.length > 35
                  ? processingTranscriptPreview.slice(0, 35) + '...'
                  : processingTranscriptPreview}"`
              : t("processing")}
          </span>
        </div>
      </div>
    );
  }

  // ── Avatar loading: show loading pill ──
  if (avatarLoading) {
    return (
      <div className="audio-capture processing-active">
        <div className="processing-pill">
          <div className="processing-spinner"></div>
          <span className="processing-label">{t("thinking")}</span>
        </div>
      </div>
    );
  }

  // ── Idle: mic pill ──
  return (
    <div className="audio-capture idle-pill-active">
      <button onClick={updateRecordingStatus} className="idle-pill" id="speech" disabled={disabled}>
        <div className="idle-pill-icon">
          <i className="fa fa-microphone" aria-hidden="true"></i>
        </div>
        <span className="idle-pill-label">{t("tapToTalk")}</span>
      </button>
    </div>
  );
};

export default Dictaphone;
